import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { expValEquals } from '@confluence/feature-experiments';
import { useRightSidebarContext } from '@confluence/page-layout-context';

import { APPROVALS_ANALYTICS_SOURCE } from './analytics.ts';
import { useApprovals } from './ApprovalsContext.tsx';
import { openApprovalsPaneForContext } from './approvalsPaneSidebar.ts';

const OVERFLOW_MENU_ENTRY_POINT = 'overflowMenu';

/**
 * Modes that get the item: the published page (`view`) and both editor modes — `edit` for a
 * published page with unpublished changes, `draft` for a page that has never been published.
 * Live docs are excluded because starting an approval on one goes through live-doc conversion.
 */
const SUPPORTED_CONTENT_MODES = new Set(['view', 'edit', 'draft']);

type UseStartApprovalOverflowMenuItemParams = {
	/** Content type from the host header — the entry point is offered on pages only */
	contentType: string | undefined;
	/** Content mode from the host header — offered on the published page and in the editor */
	contentMode: string | undefined;
	/** Whether the viewer can update this content */
	hasUpdatePermission: boolean;
	/** Archived pages cannot start a review */
	isArchived: boolean;
	/** Anonymous viewers cannot start a review */
	isLoggedIn: boolean;
};

type UseStartApprovalOverflowMenuItemResult = {
	/** Whether the host menu should render the Start approval item */
	shouldRender: boolean;
	/** Opens the approvals pane on the current version in start-approval mode */
	onStartApproval: () => void;
};

/**
 * Drives the "Start approval" item in the page overflow (kebab) menu, on both the published page
 * and in the editor.
 *
 * Unlike the byline and share-dialog entry points, this one is offered even when the space does
 * not require approval before publishing — it only needs approvals to be enabled for the space.
 * It is hidden once a review already exists on the current version, because the byline and the
 * approvals pane own that state.
 *
 * It is also hidden while the *other* version has a pending review: a page cannot have an
 * approval running on both its published version and its draft at once, so starting one would
 * fail. This mirrors the byline and details panel, which block the same case with "This page has
 * a draft in review" (see `OtherModeReviewBlockingPopup`) — the menu hides the item instead,
 * since a menu item that only explains why it can't run isn't worth the space.
 *
 * The caller owns `shouldRender` (rather than the item rendering `null` itself) so the overflow
 * menu's separator logic keeps counting real items.
 */
export const useStartApprovalOverflowMenuItem = ({
	contentType,
	contentMode,
	hasUpdatePermission,
	isArchived,
	isLoggedIn,
}: UseStartApprovalOverflowMenuItemParams): UseStartApprovalOverflowMenuItemResult => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		approvalRequest,
		contentId,
		contentStatus,
		isContentApprovalsEnabled,
		otherModeReviewStatus,
	} = useApprovals();
	const rightSidebarContext = useRightSidebarContext();

	const hasActiveReview =
		Boolean(approvalRequest?.review) && approvalRequest?.review?.reviewStatus !== 'CANCELED';
	// Same rule the byline uses before opening the pane (`otherModeReviewStatus === 'PENDING'`).
	const isBlockedByOtherVersionReview = otherModeReviewStatus === 'PENDING';

	const shouldRender =
		contentType === 'page' &&
		SUPPORTED_CONTENT_MODES.has(contentMode ?? '') &&
		isLoggedIn &&
		!isArchived &&
		hasUpdatePermission &&
		isContentApprovalsEnabled &&
		!hasActiveReview &&
		!isBlockedByOtherVersionReview &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true) &&
		expValEquals('confluence_approvals_entry_point_updates_exp', 'isEnabled', true);

	const onStartApproval = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'startApproval',
				source: APPROVALS_ANALYTICS_SOURCE,
				attributes: {
					contentId,
					// `CURRENT` from the published page's menu, `DRAFT` from the editor's.
					contentStatus,
					trigger: OVERFLOW_MENU_ENTRY_POINT,
				},
			},
		}).fire();

		// Take the content status from the provider rather than deriving it here, so the pane
		// context key matches the one every other entry point opens on the same version — and
		// `StartApprovalPane` records `entryPoint: 'overflowMenu'` against the right version on
		// its `startApprovalSubmit`, `created` and `failed` events.
		openApprovalsPaneForContext({
			contentId,
			contentStatus,
			entryPoint: OVERFLOW_MENU_ENTRY_POINT,
			rightSidebarContext,
		});
	}, [contentId, contentStatus, createAnalyticsEvent, rightSidebarContext]);

	return { shouldRender, onStartApproval };
};
